/**
 * The web's on-screen touch pad: a plain DOM overlay (no Preact: the input folder never imports the UI) feeding
 * pointer events to the input core's `TouchState` (input/touch.ts). While visible it sets
 * `data-touch-controls="on"` on <html> so the HUD can make room for the thumbs.
 */
import {
  TOUCH_LEFT,
  TOUCH_RIGHT,
  TOUCH_SLOP_PX,
  type TouchButtonSpec,
  type TouchRect,
  type TouchState,
} from '@hf/client-core/input/touch';

const STYLE_ID = 'hf-touch-style';
const CSS = `
.hf-touch{position:fixed;inset:0;z-index:40;pointer-events:none;user-select:none;-webkit-user-select:none;
  -webkit-touch-callout:none;touch-action:none;font-family:var(--font-display,'Luckiest Guy',Impact,sans-serif)}
.hf-touch[hidden]{display:none}
.hf-t-cluster{position:absolute;bottom:max(14px,env(safe-area-inset-bottom));pointer-events:auto;touch-action:none}
.hf-t-cluster.l{left:max(14px,env(safe-area-inset-left));width:clamp(190px,30vw,260px);height:clamp(150px,40vh,210px)}
.hf-t-cluster.r{--tw:clamp(210px,min(34vw,60vh),300px);right:max(14px,env(safe-area-inset-right));width:var(--tw);
  height:calc(var(--tw) * .84)}
.hf-t-btn{position:absolute;display:flex;align-items:center;justify-content:center;border-radius:50%;
  color:var(--cream,#fff8e7);background:rgba(20,12,28,.72);border:4px solid rgba(255,248,231,.9);
  box-shadow:0 5px 0 var(--ink,#140c1c),inset 0 -6px 0 rgba(0,0,0,.25);text-shadow:0 2px 0 var(--ink,#140c1c);
  letter-spacing:.02em;line-height:1;transition:transform 60ms ease-out,box-shadow 60ms ease-out,background 60ms;
  -webkit-tap-highlight-color:transparent;opacity:.9}
.hf-t-btn.on{transform:translateY(4px) scale(.96);box-shadow:0 1px 0 var(--ink,#140c1c),inset 0 -2px 0 rgba(0,0,0,.25)}
.hf-t-left,.hf-t-right{width:44%;aspect-ratio:1;top:0;font-size:clamp(26px,5vw,38px)}
.hf-t-left{left:0}.hf-t-right{right:0}
.hf-t-down{width:34%;aspect-ratio:1;bottom:0;left:33%;font-size:clamp(22px,4vw,30px)}
.hf-t-left.on,.hf-t-right.on,.hf-t-down.on{background:rgba(78,230,255,.55)}
.hf-t-action{width:40%;aspect-ratio:1;left:59%;top:47.6%;font-size:clamp(14px,2.4vw,19px);
  background:linear-gradient(180deg,var(--gold,#ffd23f),var(--gold-dk,#c99a00));color:var(--ink,#140c1c);
  text-shadow:none;border-color:var(--cream,#fff8e7)}
.hf-t-action.on{background:var(--gold-dk,#c99a00)}
.hf-t-jump{width:32%;aspect-ratio:1;left:22%;top:53.6%;font-size:clamp(13px,2.1vw,17px);background:rgba(232,52,60,.8)}
.hf-t-jump.on{background:var(--home-red-dk,#a8161f)}
.hf-t-special{width:24%;aspect-ratio:1;left:58%;top:2.4%;font-size:clamp(9px,1.5vw,12px);
  background:linear-gradient(180deg,var(--neon-pink,#ff2e88),#a3105a)}
.hf-t-special.on{background:#a3105a}
.hf-t-spin{width:20%;aspect-ratio:1;left:0;top:75%;font-size:clamp(10px,1.6vw,13px);background:rgba(47,125,246,.75)}
.hf-t-spin.on{background:var(--away-blue-dk,#1647a8)}
.hf-t-pause{position:absolute;left:max(10px,env(safe-area-inset-left));top:46%;width:44px;height:44px;border-radius:12px;
  pointer-events:auto;display:flex;align-items:center;justify-content:center;gap:5px;background:rgba(20,12,28,.6);
  border:3px solid rgba(255,248,231,.8);box-shadow:0 3px 0 var(--ink,#140c1c)}
.hf-t-pause i{display:block;width:5px;height:16px;border-radius:2px;background:var(--cream,#fff8e7)}
@media (prefers-reduced-motion: reduce){.hf-t-btn{transition:none}}
`;

export interface TouchOverlayOptions {
  /** Parent element (default document.body). */
  parent?: HTMLElement;
  onPause(): void;
}

export class TouchOverlay {
  readonly root: HTMLElement;
  private readonly buttons: { el: HTMLElement; spec: TouchButtonSpec }[] = [];
  private readonly roundBits = TOUCH_RIGHT.filter((b) => b.round).map((b) => b.bits);
  private rectsDirty = true;
  private visible = false;
  private readonly doc: Document;
  private readonly onResize = (): void => {
    this.rectsDirty = true;
  };

  constructor(
    private readonly state: TouchState,
    private readonly opts: TouchOverlayOptions,
  ) {
    const parent = opts.parent ?? document.body;
    this.doc = parent.ownerDocument;
    if (!this.doc.getElementById(STYLE_ID)) {
      const style = this.doc.createElement('style');
      style.id = STYLE_ID;
      style.textContent = CSS;
      this.doc.head.appendChild(style);
    }
    const root = this.doc.createElement('div');
    root.className = 'hf-touch';
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    const left = this.cluster('l', TOUCH_LEFT);
    const right = this.cluster('r', TOUCH_RIGHT);
    const pause = this.doc.createElement('div');
    pause.className = 'hf-t-pause';
    pause.setAttribute('role', 'button');
    pause.setAttribute('aria-label', 'Pause');
    pause.append(this.doc.createElement('i'), this.doc.createElement('i'));
    pause.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.opts.onPause();
    });
    root.append(left, right, pause);
    root.addEventListener('contextmenu', (e) => e.preventDefault());
    parent.appendChild(root);
    this.root = root;
    const win = this.doc.defaultView;
    win?.addEventListener('resize', this.onResize);
    win?.addEventListener('orientationchange', this.onResize);
  }

  private cluster(side: 'l' | 'r', specs: readonly TouchButtonSpec[]): HTMLElement {
    const c = this.doc.createElement('div');
    c.className = `hf-t-cluster ${side}`;
    for (const spec of specs) {
      const el = this.doc.createElement('div');
      el.className = `hf-t-btn ${spec.cls}`;
      el.textContent = spec.label;
      el.dataset['btn'] = spec.key;
      el.setAttribute('aria-label', spec.aria);
      c.appendChild(el);
      this.buttons.push({ el, spec });
    }
    c.addEventListener('pointerdown', this.onDown);
    c.addEventListener('pointermove', this.onMove);
    c.addEventListener('pointerup', this.onUp);
    c.addEventListener('pointercancel', this.onUp);
    c.addEventListener('lostpointercapture', this.onUp);
    return c;
  }

  /** Recompute hit rects from layout (on show, resize, orientation change). */
  private refreshRects(): void {
    if (!this.rectsDirty) return;
    this.rectsDirty = false;
    const rects: TouchRect[] = [];
    for (const { el, spec } of this.buttons) {
      const r = el.getBoundingClientRect();
      const slop = TOUCH_SLOP_PX;
      rects.push({
        bits: spec.bits,
        left: r.left - slop,
        top: r.top - slop,
        right: r.right + slop,
        bottom: r.bottom + slop,
        // Right-thumb buttons are exclusive circles (SPIN carries its own ▼+ACTION chord bits).
        ...(spec.round ? { round: true } : {}),
      });
    }
    this.state.setRects(rects);
  }

  private readonly onDown = (e: PointerEvent): void => {
    e.preventDefault();
    this.refreshRects();
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* synthetic events in tests */
    }
    this.state.down(e.pointerId, e.clientX, e.clientY);
    this.paint();
  };

  private readonly onMove = (e: PointerEvent): void => {
    this.state.move(e.pointerId, e.clientX, e.clientY);
    this.paint();
  };

  private readonly onUp = (e: PointerEvent): void => {
    this.state.up(e.pointerId);
    this.paint();
  };

  /** Reflect held bits on the buttons (pressed look). */
  private paint(): void {
    // The SPIN chord (▼+ACTION) must not light the left ▼: square buttons light from non-round fingers only.
    const held = this.state.heldWhere((bits) => !this.roundBits.includes(bits));
    for (const { el, spec } of this.buttons) {
      // Round buttons light only for the finger on them (SPIN must not light ACTION, and vice versa).
      const on = spec.round ? this.state.pressedExactly(spec.bits) : (held & spec.bits) !== 0;
      el.classList.toggle('on', on);
    }
  }

  setVisible(v: boolean): void {
    if (v === this.visible) return;
    this.visible = v;
    this.root.hidden = !v;
    this.rectsDirty = true;
    const html = this.doc.documentElement;
    if (v) html.dataset['touchControls'] = 'on';
    else delete html.dataset['touchControls'];
    if (!v) {
      this.state.releaseAll();
      this.paint();
    }
  }

  get isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.setVisible(false);
    const win = this.doc.defaultView;
    win?.removeEventListener('resize', this.onResize);
    win?.removeEventListener('orientationchange', this.onResize);
    this.root.remove();
  }
}

/** Does this browser have a touch screen? (Hybrid laptops report true; 'auto' still waits for a real touch.) */
export const hasTouchScreen = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(pointer: coarse)').matches) return true;
  } catch {
    /* ignore */
  }
  return (
    typeof navigator !== 'undefined' &&
    (navigator.maxTouchPoints ?? 0) > 0 &&
    !window.matchMedia?.('(pointer: fine)').matches
  );
};
